import { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useTelegramChannelSenders } from './newConversation/useChannelSenders';
import type { NewConversationDialogBaseProps } from './newConversation/types';

interface SendFromDialogResponse {
  conversation_id: string;
}

export default function NewTelegramConversationDialog({ open, onClose, onSuccess }: NewConversationDialogBaseProps) {
  const { toast } = useToast();
  const { senders, defaultId } = useTelegramChannelSenders(open);
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [chatRef, setChatRef] = useState('');
  const [contactName, setContactName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && defaultId && !selectedBotId) setSelectedBotId(defaultId);
  }, [open, defaultId, selectedBotId]);

  useEffect(() => {
    if (!open) {
      setSelectedBotId('');
      setChatRef('');
      setContactName('');
      setMessage('');
    }
  }, [open]);

  const selectedBot = senders.find((s) => s.id === selectedBotId) || null;

  const handleSubmit = async () => {
    if (!selectedBot) {
      toast({ title: 'Errore', description: 'Seleziona un bot Telegram', variant: 'destructive' });
      return;
    }
    if (!chatRef.trim()) {
      toast({ title: 'Errore', description: 'Inserisci Chat ID o @username Telegram', variant: 'destructive' });
      return;
    }
    if (!message.trim()) {
      toast({ title: 'Errore', description: 'Inserisci il messaggio', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post<SendFromDialogResponse>(
        `/integrations/telegram/bots/${selectedBot.integrationId}/send-from-dialog`,
        { chat_id: chatRef.trim(), text: message, contact_name: contactName || undefined },
      );
      if (res.success && res.data?.conversation_id) {
        toast({ title: 'Messaggio Telegram inviato' });
        onSuccess(res.data.conversation_id);
        onClose();
      } else {
        toast({ title: 'Errore', description: res.error || 'Invio fallito', variant: 'destructive' });
      }
    } catch (err) {
      // apiClient lancia un ApiError con il messaggio del backend in `message`
      // e l'envelope completo in `data` (`{ error: { code, message } }`).
      // Mostriamo il messaggio chiaro restituito dal backend (es. il 409
      // "il contatto deve avviare la chat con /start").
      const apiErr = err as { data?: { error?: { message?: string } | string }; message?: string };
      const dataErr = apiErr.data?.error;
      const msg =
        (typeof dataErr === 'string' ? dataErr : dataErr?.message) ?? apiErr.message ?? String(err);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Nuova Conversazione Telegram
          </DialogTitle>
          <DialogDescription>Invia un messaggio Telegram tramite uno dei bot configurati.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Bot Telegram *</Label>
            <Select value={selectedBotId} onValueChange={setSelectedBotId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={senders.length === 0 ? 'Nessun bot Telegram configurato' : 'Seleziona bot mittente'} />
              </SelectTrigger>
              <SelectContent>
                {senders.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="flex items-center gap-2">
                      <Send className="h-3.5 w-3.5" />
                      {s.display}
                      {s.label && <span className="text-muted-foreground">({s.label})</span>}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Chat ID o @username Telegram *</Label>
            <Input
              value={chatRef}
              onChange={(e) => setChatRef(e.target.value)}
              placeholder="@utente o 123456789"
              className="mt-1"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Il destinatario deve aver già iniziato una chat con il bot per ricevere messaggi.
            </p>
          </div>

          <div>
            <Label>Nome Contatto (opzionale)</Label>
            <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Mario Rossi" className="mt-1" />
          </div>

          <div>
            <Label>Messaggio *</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Scrivi il tuo messaggio Telegram..."
              rows={4}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Invia Telegram
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
